import React, { useCallback, useMemo, useState } from 'react';
import { InternalApiCustomListSchemaField } from '../../api-internal';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
  MenuItem,
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvButton,
  SalvMenuButton,
  SalvTable,
  Size,
} from '@salvapp/ui-components';
import { useDispatch } from 'react-redux';
import Has from '../../auth/Has';
import { CAN_ADD_CUSTOM_LIST_SCHEMA_FIELD, CAN_MODIFY_CUSTOM_LIST_SCHEMA_FIELD } from '../../auth/permissions';
import SchemaFieldModal from './SchemaFieldModal';
import { createColumnHelper } from '@tanstack/react-table';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import {
  useQueryCustomListSchemaFields,
  useSortCustomListSchemaFields,
  useUpdateCustomListEntitySchemaFields,
} from './customListSchemaActions';
import { useMe } from '../../organisation/useMe';

interface Props {
  customListId: string;
  canEdit: boolean;
}

export const SchemaFields = ({ customListId, canEdit }: Props) => {
  const dispatch = useDispatch();

  const { data: schemaFields, refetch: refetch } = useQueryCustomListSchemaFields(customListId);
  const { mutate: updateSchemaFields } = useUpdateCustomListEntitySchemaFields(customListId);
  const { mutate: sortSchemaFields } = useSortCustomListSchemaFields(customListId);

  const [showModal, setShowModal] = useState(false);
  const [schemaFieldInEditing, setSchemaFieldInEditing] = useState<InternalApiCustomListSchemaField>();
  const { permissions } = useMe();

  const canModifySchema = permissions.includes(CAN_MODIFY_CUSTOM_LIST_SCHEMA_FIELD);

  const parentSchemaFields = useMemo(() => {
    return schemaFields?.filter((schemaField) => !schemaField.parentId);
  }, [schemaFields]);

  const childSchemaFields = useMemo(() => {
    return schemaFields?.filter((schemaField) => !!schemaField.parentId);
  }, [schemaFields]);

  const handleUpdate = async () => {
    setShowModal(false);
    setSchemaFieldInEditing(undefined);
  };

  const onColumnSort = useCallback(
    (sortedColumns: InternalApiCustomListSchemaField[]) => {
      return sortSchemaFields([
        ...sortedColumns.map((schemaField, index) => {
          return {
            id: schemaField.id,
            order: index + 1,
          };
        }),
        ...(childSchemaFields?.map((schemaField) => {
          return {
            id: schemaField.id,
            order: schemaField.sortOrder,
          };
        }) ?? []),
      ]);
    },
    [childSchemaFields]
  );

  const columnHelper = createColumnHelper<InternalApiCustomListSchemaField>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('displayName', { header: 'Display name' }),
      columnHelper.accessor('fieldName', { header: 'Field name' }),
      columnHelper.accessor('type', {
        header: 'Type',
        cell: ({ getValue, row }) => (row.original.isMulti ? 'Array of ' : '') + getValue(),
      }),
      columnHelper.display({
        header: 'Visibility',
        cell: ({ row }) => (
          <>
            {row.original.visibleInMatches && row.original.visibleInList && 'Match, list'}
            {row.original.visibleInMatches && !row.original.visibleInList && 'Match'}
            {row.original.visibleInList && !row.original.visibleInMatches && 'List'}
            {!row.original.visibleInList && !row.original.visibleInMatches && '--'}
          </>
        ),
      }),
      columnHelper.accessor('isNullable', {
        header: 'Required',
        cell: ({ getValue }) => (!getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.display({
        id: 'actions',
        size: 0,
        cell: ({ row }) =>
          canEdit && (
            <Has allPermissions={[CAN_MODIFY_CUSTOM_LIST_SCHEMA_FIELD]}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <SalvMenuButton
                  size={Size.SMALL}
                  placement={'bottom end'}
                  ariaLabel={`Actions of field ${row.original.displayName}`}
                  onAction={([action]) => {
                    if (action === 'edit') {
                      setShowModal(true);
                      setSchemaFieldInEditing(schemaFields?.find((field) => field.id === row.original.id));
                    } else if (action === 'delete') {
                      dispatch(
                        showConfirmationModal({
                          title: 'Do you want to delete this schema field?',
                          confirmationText: 'Yes',
                          confirmButtonVariant: 'danger',
                          cancelText: 'No, keep it',
                          onConfirm: async () => {
                            updateSchemaFields(
                              schemaFields?.filter(
                                (field) => field.id !== row.original.id && field.parentId !== row.original.id
                              ) ?? []
                            );
                          },
                        })
                      );
                    }
                  }}
                >
                  <MenuItem title={'Edit'} value={'edit'} />
                  <MenuItem title={'Delete'} value={'delete'} />
                </SalvMenuButton>
              </div>
            </Has>
          ),
      }),
    ],
    [canEdit, schemaFields]
  );

  return (
    <>
      <div className='m-b-24'>
        <SalvBanner
          accent={SalvBannerAccent.LIGHT}
          message='Review Significant Update Indicators after schema changes to ensure changes are reflected in your SUI configuration.'
          type={SalvBannerType.INFO}
        />
      </div>
      {parentSchemaFields && (
        <SalvTable
          title={'Schema fields'}
          columns={columns}
          data={parentSchemaFields}
          striped
          emptyMessage={'Schema is not yet defined'}
          className={'m-b-24'}
          actions={
            canEdit && (
              <Has allPermissions={[CAN_ADD_CUSTOM_LIST_SCHEMA_FIELD]}>
                <SalvButton
                  label='New field'
                  icon={faPlus}
                  onClick={() => setShowModal(true)}
                  variant='primary'
                  size={Size.SMALL}
                />
              </Has>
            )
          }
          onManualSort={canModifySchema ? onColumnSort : undefined}
        />
      )}
      <SchemaFieldModal
        schemaFields={schemaFields}
        customListId={customListId}
        onChange={handleUpdate}
        onError={refetch}
        onClose={() => {
          setShowModal(false);
          setSchemaFieldInEditing(undefined);
        }}
        show={showModal}
        setShow={setShowModal}
        schemaField={schemaFieldInEditing}
      />
    </>
  );
};

export default SchemaFields;
